import React, { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { motion } from 'framer-motion';
import {
  BookOpen,
  DollarSign,
  Heart,
  Plane,
  Users,
  Menu
} from 'lucide-react';
import { UserProfile } from './UserProfile';
import { AIStatusIndicator } from './ai/AIStatusIndicator';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';

interface NavLink {
  path: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  gradient: string;
}

const NAV_LINKS: NavLink[] = [
  {
    path: '/life/stories',
    label: 'LIFE',
    icon: <BookOpen className="w-5 h-5" />,
    color: 'text-purple-400',
    gradient: 'from-purple-500 to-pink-500'
  },
  {
    path: '/money/decisions',
    label: 'MONEY',
    icon: <DollarSign className="w-5 h-5" />,
    color: 'text-emerald-400',
    gradient: 'from-emerald-500 to-teal-500'
  },
  {
    path: '/joy',
    label: 'JOY',
    icon: <Heart className="w-5 h-5" />,
    color: 'text-rose-400',
    gradient: 'from-rose-500 to-orange-500'
  },
  {
    path: '/flight',
    label: 'Flight',
    icon: <Plane className="w-5 h-5" />,
    color: 'text-cyan-400',
    gradient: 'from-cyan-500 to-blue-500'
  },
  {
    path: '/community',
    label: 'Community',
    icon: <Users className="w-5 h-5" />,
    color: 'text-amber-400',
    gradient: 'from-amber-500 to-orange-500'
  },
];

export const Navigation: React.FC = () => {
  const [location] = useLocation();
  const { isAuthenticated } = useSupabaseAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Don't show navigation on home page
  if (location === '/') {
    return null;
  }

  // Only show Flight in top nav
  // LIFE, MONEY, JOY are in ModuleNavBar on each page
  // Community and AI are accessed via direct routes or status indicator
  const otherLinks = NAV_LINKS.filter(link =>
    link.label === 'Flight'
  );

  return (
    <div className="fixed top-0 left-0 right-0 z-nav pt-safe">
      {/* Navigation bar - always visible */}
      <nav
        className="bg-black/60 backdrop-blur-md border-b border-white/10"
        role="navigation"
        aria-label="Main navigation"
      >
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-14 sm:h-16">
              {/* Logo / Brand - Hidden on desktop since it's minimal */}
              <div className="flex items-center lg:hidden">
                <Link href="/">
                  <a className="text-xl font-bold text-white">
                    Hover
                  </a>
                </Link>
              </div>

              {/* Desktop Navigation - Hidden on mobile */}
              <div className="hidden lg:flex items-center gap-2 ml-auto">
                {otherLinks.map((link) => {
                  const isActive = location.startsWith(link.path);

                  return (
                    <Link key={link.path} href={link.path}>
                      <a>
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`
                            flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-sm font-medium
                            focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-black/20
                            ${isActive
                              ? `bg-gradient-to-r ${link.gradient} text-white shadow-lg`
                              : 'text-gray-300 hover:bg-white/10 hover:text-white'
                            }
                          `}
                          tabIndex={0}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          {link.icon}
                          <span>{link.label}</span>
                        </motion.div>
                      </a>
                    </Link>
                  );
                })}

                {/* AI Status Indicator - Desktop */}
                {isAuthenticated && (
                  <div className="ml-2">
                    <AIStatusIndicator />
                  </div>
                )}

                {/* User Profile or Sign In - Desktop */}
                <div className="ml-2">
                  {isAuthenticated ? (
                    <UserProfile />
                  ) : (
                    <Link href="/">
                      <a>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium shadow-lg hover:shadow-xl transition-shadow"
                        >
                          Sign In
                        </motion.button>
                      </a>
                    </Link>
                  )}
                </div>
              </div>

              {/* Mobile Navigation */}
              <div className="flex items-center gap-2 lg:hidden">
                {/* AI Status Indicator - Mobile */}
                {isAuthenticated && (
                  <AIStatusIndicator />
                )}

                {/* User Profile - Mobile */}
                {isAuthenticated && (
                  <UserProfile />
                )}

                {/* Mobile Menu Toggle */}
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                  <SheetTrigger asChild>
                    <button
                      className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                      aria-label="Open navigation menu"
                    >
                      <Menu className="w-6 h-6 text-white" />
                    </button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[85vw] sm:w-[400px] max-w-[400px]">
                    <SheetHeader>
                      <SheetTitle>Navigation</SheetTitle>
                    </SheetHeader>
                    <div className="mt-8 flex flex-col gap-4">
                      {/* All Navigation Links - Mobile */}
                      {NAV_LINKS.map((link) => {
                        const isActive = location.startsWith(link.path);

                        return (
                          <Link key={link.path} href={link.path}>
                            <a onClick={() => setMobileMenuOpen(false)}>
                              <motion.div
                                whileTap={{ scale: 0.95 }}
                                className={`
                                  flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                                  ${isActive
                                    ? `bg-gradient-to-r ${link.gradient} text-white shadow-lg`
                                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                                  }
                                `}
                              >
                                {link.icon}
                                <span className="font-medium">{link.label}</span>
                              </motion.div>
                            </a>
                          </Link>
                        );
                      })}
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
      </nav>
    </div>
  );
};